import { useEffect, useState } from 'react';
import { BlockStack, Card, Text, List, Link, SkeletonBodyText, InlineStack, Icon } from '@shopify/polaris';
import { PlayCircleIcon } from '@shopify/polaris-icons';
import { useFeedStatus } from '../../hooks/useFeed';

interface AiCrawlerSetupGuideProps {
  /** Opens the app embed setup video dialog. */
  onPlayEmbedVideo?: () => void;
  /** Incrementing counter that triggers a shake/rotate of the video icon. */
  shakeSignal?: number;
}

/**
 * F4.3: AI Crawler Setup Guide sidebar card.
 * Reads the real feed URLs from useFeedStatus so the Sitemap directive
 * shows the actual ai-sitemap.xml URL rather than a placeholder.
 */
export function AiCrawlerSetupGuide({ onPlayEmbedVideo, shakeSignal }: AiCrawlerSetupGuideProps) {
  const { data: feed, isLoading } = useFeedStatus();
  const [shaking, setShaking] = useState(false);

  useEffect(() => {
    if (!shakeSignal) return;
    setShaking(true);
    const t = setTimeout(() => setShaking(false), 700);
    return () => clearTimeout(t);
  }, [shakeSignal]);

  const sitemapUrl = feed?.feedUrl
    ? feed.feedUrl.replace('feeds/products.json', 'feeds/ai-sitemap.xml')
    : null;

  const robotsHintsUrl = feed?.feedUrl
    ? feed.feedUrl.replace('feeds/products.json', 'feeds/robots-hints.txt')
    : null;

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingSm">🛠️ AI Crawler Setup</Text>
        <Text as="p" variant="bodySm" tone="subdued">
          Complete these steps so AI crawlers can discover and index your products.
        </Text>

        {/* Step 1 */}
        <BlockStack gap="100">
          <InlineStack gap="100" blockAlign="center">
            <Text as="p" variant="bodySm" fontWeight="semibold">Step 1 — Enable the App Embed</Text>
            {onPlayEmbedVideo && (
              <button
                type="button"
                className={`feed-embed-video-btn${shaking ? ' feed-embed-video-btn--shake' : ''}`}
                onClick={onPlayEmbedVideo}
                aria-label="Watch how to enable the app embed"
              >
                <Icon source={PlayCircleIcon} />
              </button>
            )}
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            Enable <strong>Zelium AI Structured Data</strong> in{' '}
            <Link url="https://admin.shopify.com/store/uff-ohh/themes" external>
              Online Store → Themes → Customize → App embeds
            </Link>.
            This injects a{' '}<code>{'<link rel="alternate">'}</code>{' '}tag into
            your store's {'<head>'} so AI crawlers can discover your llms.txt
            automatically from any page.
          </Text>
        </BlockStack>

        {/* Step 2 */}
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" fontWeight="semibold">Step 2 — Update your robots.txt</Text>
          <Text as="p" variant="bodySm" tone="subdued">
            In Shopify go to{' '}
            <Link url="https://admin.shopify.com/store/uff-ohh/themes" external>
              Online Store → Themes → Edit code
            </Link>
            {' '}and open <strong>templates/robots.txt.liquid</strong>.
            Copy the directives from your{' '}
            {robotsHintsUrl ? (
              <Link url={robotsHintsUrl} external>robots-hints.txt</Link>
            ) : (
              <strong>robots-hints.txt</strong>
            )}
            {' '}and paste them at the bottom. This tells GPTBot, ClaudeBot,
            Google-Extended, Bingbot, and PerplexityBot they can read your feed.
          </Text>
        </BlockStack>

        {/* Step 3 */}
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" fontWeight="semibold">Step 3 — Add a Sitemap directive</Text>
          <Text as="p" variant="bodySm" tone="subdued">
            At the very end of <strong>robots.txt.liquid</strong>, add this line:
          </Text>
          {isLoading ? (
            <SkeletonBodyText lines={1} />
          ) : sitemapUrl ? (
            <div style={{
              background: 'var(--p-color-bg-surface-secondary)',
              borderRadius: '6px',
              padding: '8px 10px',
              fontFamily: 'monospace',
              fontSize: '12px',
              wordBreak: 'break-all',
              userSelect: 'all',
            }}>
              {`Sitemap: ${sitemapUrl}`}
            </div>
          ) : (
            <Text as="p" variant="bodySm" tone="subdued">
              Activate your feed first to get the sitemap URL.
            </Text>
          )}
          <Text as="p" variant="bodySm" tone="subdued">
            This registers your AI product sitemap with all crawlers that parse robots.txt.
          </Text>
        </BlockStack>

        {/* What Zelium handles */}
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" fontWeight="semibold">What Zelium does automatically:</Text>
          <List type="bullet">
            <List.Item>
              <Text as="span" variant="bodySm">Generates and hosts llms.txt, robots-hints.txt, and ai-sitemap.xml daily</Text>
            </List.Item>
            <List.Item>
              <Text as="span" variant="bodySm">Logs every AI crawler visit to your feed</Text>
            </List.Item>
            <List.Item>
              <Text as="span" variant="bodySm">Lets you block specific crawlers from the Analytics panel</Text>
            </List.Item>
          </List>
        </BlockStack>
      </BlockStack>
    </Card>
  );
}
